import { useMarketplaceLabel, marketplaceChartRows } from "../configurazione/marketplaceDisplay";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { PieChart } from "@mui/x-charts/PieChart";
import { cheerfulFiestaPalette } from "@mui/x-charts/colorPalettes";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import { DonutChartSkeleton } from "../../interfaccia/loading";

const coloriTorte = cheerfulFiestaPalette("light");

const euro = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
});

function meseScorso() {
  const oggi = new Date();
  const inizio = new Date(oggi.getFullYear(), oggi.getMonth() - 1, 1);
  const fine = new Date(oggi.getFullYear(), oggi.getMonth(), 0);
  const data = (value) =>
    [value.getFullYear(), String(value.getMonth() + 1).padStart(2, "0"), String(value.getDate()).padStart(2, "0")].join("-");
  return { dataStart: data(inizio), dataEnd: data(fine) };
}

function TortaIncassi({ titolo, righe }) {
  const categorie = (righe || []).map((riga) => ({
    id: riga.id,
    label: riga.label || "N/D",
    value: Number(riga.value || 0),
  }));
  const totale = categorie.reduce((somma, riga) => somma + riga.value, 0);
  const visibili = categorie.filter((riga) => riga.value !== 0);
  const fette = visibili.filter((riga) => riga.value > 0);

  return (
    <div className="w-100 w-50-l pa3 flex flex-column items-center">
      <h3 className="f5 ma0 mb2">{titolo}</h3>
      <div className="f4 b mb3">{euro.format(totale)}</div>
      {fette.length > 0 ? (
        <PieChart
          series={[{ data: fette, innerRadius: 48, outerRadius: 105, cx: 140, cy: 110 }]}
          colors={coloriTorte}
          width={280}
          height={225}
          legend={{ hidden: true }}
        />
      ) : (
        <div className="gray pa4">Nessun incasso nel periodo</div>
      )}
      {visibili.length > 0 && (
        <div className="w-100" style={{ maxWidth: 420 }}>
          {visibili.map((riga) => (
            <div key={riga.id ?? riga.label} className="flex justify-between items-baseline pv1 bb b--black-10">
              <span className="tl pr2 flex items-center">
                {riga.value > 0 && (
                  <span className="dib mr2 flex-none" style={{ width: 10, height: 10, backgroundColor: coloriTorte[fette.indexOf(riga) % coloriTorte.length] }} />
                )}
                {riga.label}
              </span>
              <strong className="tr nowrap">{euro.format(riga.value)}</strong>
            </div>
          ))}
        </div>
      )}
      {visibili.some((riga) => riga.value < 0) && (
        <div className="f7 gray mt2">Gli importi negativi concorrono al totale, ma non alla torta.</div>
      )}
    </div>
  );
}

export default function StatIncassiSpedizioni() {
  const marketplaceLabel = useMarketplaceLabel();
  const { getAccessTokenSilently } = useAuth0();
  const [date, setDate] = useState(meseScorso);
  const [range, setRange] = useState(meseScorso);
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    async function carica() {
      setLoading(true);
      setErrore("");
      try {
        const token = await getAccessTokenSilently();
        if (controller.signal.aborted) return;
        const response = await axios.get(`${apiUrl}/logistica/dashboard/incassiSpedizioni`, {
          params: range,
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setDati(response.data);
      } catch (error) {
        if (!controller.signal.aborted && !axios.isCancel(error)) {
          setErrore("Errore nel caricamento degli incassi delle spedizioni");
          setDati(null);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    carica();
    return () => controller.abort();
  }, [getAccessTokenSilently, range]);

  const applicaRange = (event) => {
    event.preventDefault();
    if (date.dataStart && date.dataEnd && date.dataStart <= date.dataEnd) {
      setRange({ ...date });
    }
  };

  return (
    <div className="sfondoBiancoTema pa3 tc">
      <form onSubmit={applicaRange} className="flex flex-wrap justify-center items-center mb3">
        <div className="pa2 mw5">
          <label className="mr1 b" htmlFor="incassi-dataStart">Da data</label>
          <input id="incassi-dataStart" type="date" required className="input-reset inputOrizzontale"
            value={date.dataStart} max={date.dataEnd || undefined}
            onChange={(event) => setDate({ ...date, dataStart: event.target.value })} />
        </div>
        <div className="pa2 mw5">
          <label className="mr1 b" htmlFor="incassi-dataEnd">a data</label>
          <input id="incassi-dataEnd" type="date" required className="input-reset inputOrizzontale"
            value={date.dataEnd} min={date.dataStart || undefined}
            onChange={(event) => setDate({ ...date, dataEnd: event.target.value })} />
        </div>
        <button type="submit" className="dim tastoInputOrizzontaleNoRadNoHeight br2 ml3">
          {stilePulsanti.tastoCercaAspetto}
        </button>
      </form>
      <div className="gray mb3">Incassi dal {range.dataStart} al {range.dataEnd}</div>
      {loading && (
        <div className="flex flex-wrap justify-center">
          <DonutChartSkeleton tone="soft" width={300} height={330} />
          <DonutChartSkeleton tone="soft" width={300} height={330} />
        </div>
      )}
      {!loading && errore && <div className="dark-red">{errore}</div>}
      {!loading && !errore && dati && (
        <div className="flex flex-wrap items-start justify-center">
          <TortaIncassi titolo="Marketplace" righe={marketplaceChartRows(dati.marketplace, marketplaceLabel)} />
          <TortaIncassi titolo="Paese di provenienza" righe={dati.paesi} />
        </div>
      )}
    </div>
  );
}
